import { LitElement, html, nothing } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import type { AgentChatMessage } from './cp-agent-chat-message.js';
import type { ChatConversationAgent, ChatAttachmentInfo } from '../../services/chat-api.js';
import { uploadAttachment, deleteAttachment, getAttachmentUrl } from '../../services/chat-api.js';
import './cp-agent-chat-message.js';

const ACCEPTED_TYPES = 'image/png,image/jpeg,image/webp,image/gif,application/pdf,text/plain,text/markdown,text/csv';

@customElement('cp-agent-chat-panel')
export class CpAgentChatPanel extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Array }) messages: AgentChatMessage[] = [];
  @property({ type: Boolean }) isStreaming = false;
  @property({ type: Object }) activeAgent: ChatConversationAgent | null = null;
  @property({ type: Number }) conversationId: number | null = null;
  @property({ type: Array }) pendingAttachments: ChatAttachmentInfo[] = [];

  @state() private _inputValue = '';
  @state() private _isDragOver = false;
  @state() private _isUploading = false;
  @query('.cp-agent-chat-panel__messages') private _messagesContainer!: HTMLElement;
  @query('.cp-agent-chat-panel__input') private _inputEl!: HTMLTextAreaElement;

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);
    if (changedProperties.has('messages') || changedProperties.has('isStreaming')) {
      this._scrollToBottom();
    }
  }

  private _scrollToBottom(): void {
    requestAnimationFrame(() => {
      const messages = this._messagesContainer?.querySelectorAll('cp-agent-chat-message');
      if (messages && messages.length > 0) {
        messages[messages.length - 1].scrollIntoView({ behavior: 'smooth', block: 'end' });
      } else if (this._messagesContainer) {
        this._messagesContainer.scrollTop = this._messagesContainer.scrollHeight;
      }
    });
  }

  private _onInput(e: Event): void {
    const textarea = e.target as HTMLTextAreaElement;
    this._inputValue = textarea.value;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }

  private _onKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this._sendMessage();
    }
  }

  private _sendMessage(): void {
    const content = this._inputValue.trim();
    if ((!content && this.pendingAttachments.length === 0) || this.isStreaming) return;

    const attachmentIds = this.pendingAttachments.map(a => a.id);

    this.dispatchEvent(new CustomEvent('message-send', {
      bubbles: true,
      composed: true,
      detail: { content: content || '(Dateien angehaengt)', attachmentIds },
    }));

    this._inputValue = '';
    if (this._inputEl) {
      this._inputEl.value = '';
      this._inputEl.style.height = 'auto';
      this._inputEl.focus();
    }
  }

  // ── File Upload Handling ──

  private async _handleFiles(files: FileList | File[]): Promise<void> {
    if (!this.conversationId || this._isUploading) return;

    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;

    this._isUploading = true;
    this.requestUpdate();

    for (const file of fileArray) {
      try {
        const attachment = await uploadAttachment(this.conversationId, file);
        this.dispatchEvent(new CustomEvent('attachment-added', {
          bubbles: true,
          composed: true,
          detail: attachment,
        }));
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }

    this._isUploading = false;
    this.requestUpdate();
  }

  private _onAttachClick(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = ACCEPTED_TYPES;
    input.addEventListener('change', () => {
      if (input.files && input.files.length > 0) {
        void this._handleFiles(input.files);
      }
    });
    input.click();
  }

  private _onDragOver(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();
    this._isDragOver = true;
  }

  private _onDragLeave(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();
    this._isDragOver = false;
  }

  private _onDrop(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();
    this._isDragOver = false;
    if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
      void this._handleFiles(e.dataTransfer.files);
    }
  }

  private _onPaste(e: ClipboardEvent): void {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files: File[] = [];
    for (const item of Array.from(items)) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      void this._handleFiles(files);
    }
  }

  private _onRemoveAttachment(id: number): void {
    void deleteAttachment(id).catch(err => console.warn('Delete attachment failed:', err));
    this.dispatchEvent(new CustomEvent('attachment-removed', {
      bubbles: true,
      composed: true,
      detail: { id },
    }));
  }

  private _isImage(mime: string): boolean {
    return mime.startsWith('image/');
  }

  private _formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private _renderEmptyState() {
    return html`
      <div class="cp-agent-chat-panel__empty">
        <span class="cp-agent-chat-panel__empty-text">Stelle eine Frage oder starte die Konversation...</span>
      </div>
    `;
  }

  private _renderTypingIndicator() {
    return html`
      <div class="cp-agent-chat-panel__typing">
        <div class="cp-agent-chat-panel__typing-line"></div>
        <div class="cp-agent-chat-panel__typing-line"></div>
        <div class="cp-agent-chat-panel__typing-line"></div>
      </div>
    `;
  }

  private _renderPendingStrip() {
    if (this.pendingAttachments.length === 0 && !this._isUploading) return nothing;

    return html`
      <div class="cp-agent-chat-panel__pending-strip">
        ${this._isUploading ? html`
          <div class="cp-agent-chat-panel__upload-indicator">
            <span class="cp-agent-chat-panel__upload-spinner"></span>
            <span>Hochladen...</span>
          </div>
        ` : nothing}
        ${this.pendingAttachments.map(att => html`
          <div class="cp-agent-chat-panel__pending-item">
            ${this._isImage(att.mime_type) ? html`
              <img
                class="cp-agent-chat-panel__pending-thumb"
                src="${getAttachmentUrl(att.id)}"
                alt="${att.file_name}"
              />
            ` : html`
              <div class="cp-agent-chat-panel__pending-file">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
                <span class="cp-agent-chat-panel__pending-name">${att.file_name}</span>
                <span class="cp-agent-chat-panel__pending-size">${this._formatFileSize(att.file_size)}</span>
              </div>
            `}
            <button
              class="cp-agent-chat-panel__pending-remove"
              @click=${() => this._onRemoveAttachment(att.id)}
              title="Entfernen"
            >&times;</button>
          </div>
        `)}
      </div>
    `;
  }

  render() {
    const hasMessages = this.messages.length > 0;
    const lastIndex = this.messages.length - 1;
    const canSend = (this._inputValue.trim() || this.pendingAttachments.length > 0) && !this.isStreaming;

    return html`
      <div class="cp-agent-chat-panel">
        <div class="cp-agent-chat-panel__messages">
          ${hasMessages
            ? this.messages.map((msg, i) => html`
                <cp-agent-chat-message
                  .message=${msg}
                  .agent=${this.activeAgent}
                  ?isStreaming=${this.isStreaming && i === lastIndex && msg.role === 'assistant'}
                ></cp-agent-chat-message>
              `)
            : this._renderEmptyState()
          }
          ${this.isStreaming && (!hasMessages || this.messages[lastIndex]?.role !== 'assistant')
            ? this._renderTypingIndicator()
            : nothing
          }
        </div>

        <div
          class="cp-agent-chat-panel__input-area ${this._isDragOver ? 'cp-agent-chat-panel__input-area--dragover' : ''}"
          @dragover=${this._onDragOver}
          @dragleave=${this._onDragLeave}
          @drop=${this._onDrop}
        >
          ${this._renderPendingStrip()}
          <div class="cp-agent-chat-panel__input-row">
            <button
              class="cp-agent-chat-panel__attach"
              @click=${this._onAttachClick}
              ?disabled=${this.isStreaming || !this.conversationId}
              title="Datei anhaengen"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
              </svg>
            </button>
            <textarea
              class="cp-agent-chat-panel__input"
              placeholder="Nachricht eingeben... (Enter zum Senden, Shift+Enter fur Zeilenumbruch)"
              rows="3"
              .value=${this._inputValue}
              @input=${this._onInput}
              @keydown=${this._onKeyDown}
              @paste=${this._onPaste}
              ?disabled=${this.isStreaming}
            ></textarea>
            <button
              class="cp-agent-chat-panel__send"
              @click=${this._sendMessage}
              ?disabled=${!canSend}
              title="Senden"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            </button>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-agent-chat-panel': CpAgentChatPanel;
  }
}
